import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';
import { RotateCcw, CheckCircle, Shield, Tag, Loader } from 'lucide-react';

const FactoryReturnedPage: React.FC = () => {
  const { products: filteredProducts, loading, error } = useProducts({ productType: 'factory-returned' });

  useEffect(() => {
    document.title = "Factory Returned | Strum Music";
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading factory returned items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading factory returned items</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <RotateCcw className="h-16 w-16 mx-auto mb-4 text-purple-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Factory Returned
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Professional-grade instruments that have been returned to the factory, 
              fully refurbished and certified at significant savings.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Factory Returned Benefits */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Tag className="h-8 w-8 text-purple-600" />,
                title: "Maximum Savings",
                description: "Save up to 30% off retail price on factory-certified instruments."
              },
              {
                icon: <CheckCircle className="h-8 w-8 text-purple-600" />,
                title: "Factory Certified",
                description: "Fully refurbished and tested by authorized manufacturer technicians."
              },
              {
                icon: <Shield className="h-8 w-8 text-purple-600" />,
                title: "Warranty Included",
                description: "6-month manufacturer warranty with perfect working condition guarantee."
              }
            ].map((benefit, index) => (
              <motion.div 
                key={index}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-purple-100 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <div className="container-custom py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Factory Returned Items</h2>
        
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative"
              >
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-purple-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    Factory Returned
                  </span>
                </div>
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <RotateCcw className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Factory Returned Items Available</h3>
            <p className="text-gray-600">Check back soon for new factory returned deals.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FactoryReturnedPage;